import * as Sentry from "@sentry/react";
import { env } from "@shared/config";

/**
 * Call once in main.tsx before createRoot(). No-op when VITE_SENTRY_DSN is unset,
 * so local dev machines never send events.
 */
export function initSentry(): void {
  if (!env.sentry.dsn) return;

  Sentry.init({
    dsn: env.sentry.dsn,
    environment: env.sentry.environment,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: env.sentry.tracesSampleRate,
    // Propagate sentry-trace + baggage headers to the API so backend spans are linked
    tracePropagationTargets: [env.api.url],
    beforeSend(event) {
      // Strip Authorization header from captured request data to avoid leaking tokens
      if (event.request?.headers) {
        const headers = event.request.headers as Record<string, string>;
        delete headers["Authorization"];
        delete headers["authorization"];
      }
      return event;
    },
  });
}

// All features/providers call through this wrapper — never @sentry/react directly
export const sentryService = {
  captureException: (err: unknown, context?: string) =>
    Sentry.captureException(err, context ? { data: { context } } : undefined),

  captureMessage: (msg: string, level?: Sentry.SeverityLevel) =>
    Sentry.captureMessage(msg, level),

  setUser: (user: { id: string; email?: string }) => Sentry.setUser(user),

  clearUser: () => Sentry.setUser(null),

  addBreadcrumb: (breadcrumb: Sentry.Breadcrumb) =>
    Sentry.addBreadcrumb(breadcrumb),
};
